import { useEffect, useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { MoonStars, Receipt, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const TIMEZONES = ["Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Berlin", "Europe/Madrid", "Europe/Lisbon", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Asia/Dubai", "Asia/Singapore", "Australia/Sydney", "UTC"];

/** Settings → Notifications: Alert Quiet Hours + "still unpaid" invoice reminder schedule (both tenant-wide). */
export default function TenantAlertSettings() {
  const [qh, setQh] = useState(null);
  const [rem, setRem] = useState(null);
  const [newDay, setNewDay] = useState("");
  const [saving, setSaving] = useState("");

  useEffect(() => {
    API.get("/tenant/notification-settings").then(({ data }) => { setQh(data.quiet_hours); setRem(data.invoice_reminders); }).catch(() => {});
  }, []);

  const save = async (patch, which) => {
    setSaving(which);
    try {
      const { data } = await API.put("/tenant/notification-settings", patch);
      if (data.quiet_hours) setQh(data.quiet_hours);
      if (data.invoice_reminders) setRem(data.invoice_reminders);
      toast.success(which === "qh" ? (data.quiet_hours?.enabled ? `Quiet hours on — ${data.quiet_hours.start} to ${data.quiet_hours.end}` : "Quiet hours off") : "Invoice reminder schedule saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving("");
    }
  };

  if (!qh || !rem) return null;

  const addDay = () => {
    const n = parseInt(newDay, 10);
    if (!n || n < 1 || n > 120) return toast.error("Enter a day count between 1 and 120");
    if (rem.overdue_days.includes(n)) return setNewDay("");
    setRem({ ...rem, overdue_days: [...rem.overdue_days, n].sort((a, b) => a - b) });
    setNewDay("");
  };

  return (
    <>
      <div className="border border-border rounded-md p-4 mb-6" data-testid="quiet-hours-card">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-sm font-semibold font-[Chivo] flex items-center gap-1.5"><MoonStars size={15} weight="duotone" /> Alert Quiet Hours</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Hold non-critical automated pings (daily briefings, digests, nudges, follow-ups) overnight and deliver them as one "While you were away" summary when quiet hours end.
              Emergencies, SLA breaches, security alerts and payment failures always break through; actions people take live are never held.
            </p>
          </div>
          <Switch checked={qh.enabled} onCheckedChange={(v) => setQh({ ...qh, enabled: v })} data-testid="quiet-hours-enabled" />
        </div>
        <div className={`grid grid-cols-2 sm:grid-cols-4 gap-3 ${qh.enabled ? "" : "opacity-60"}`}>
          <div>
            <Label className="text-xs">Start</Label>
            <Input type="time" value={qh.start} onChange={(e) => setQh({ ...qh, start: e.target.value })} disabled={!qh.enabled} data-testid="quiet-hours-start" />
          </div>
          <div>
            <Label className="text-xs">End</Label>
            <Input type="time" value={qh.end} onChange={(e) => setQh({ ...qh, end: e.target.value })} disabled={!qh.enabled} data-testid="quiet-hours-end" />
          </div>
          <div>
            <Label className="text-xs">Timezone</Label>
            <select value={qh.timezone} onChange={(e) => setQh({ ...qh, timezone: e.target.value })} disabled={!qh.enabled} className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-xs" data-testid="quiet-hours-timezone">
              {[...new Set([qh.timezone, ...TIMEZONES])].map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs pt-6 cursor-pointer">
            <Switch checked={qh.weekends} onCheckedChange={(v) => setQh({ ...qh, weekends: v })} disabled={!qh.enabled} data-testid="quiet-hours-weekends" /> All weekend
          </label>
        </div>
        <div className="flex justify-end mt-3">
          <Button size="sm" onClick={() => save({ quiet_hours: qh }, "qh")} disabled={saving === "qh"} data-testid="quiet-hours-save-btn">{saving === "qh" ? "Saving..." : "Save"}</Button>
        </div>
      </div>

      <div className="border border-border rounded-md p-4 mb-6" data-testid="invoice-reminders-card">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-sm font-semibold font-[Chivo] flex items-center gap-1.5"><Receipt size={15} weight="duotone" /> Unpaid Invoice Chasers</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Automatic "invoice still unpaid" emails with a one-tap Pay Online button, sent N days after the due date to your client's portal users and their billing contact.
              (Your due-soon and first-overdue reminders always run.)
            </p>
          </div>
          <Switch checked={rem.enabled} onCheckedChange={(v) => setRem({ ...rem, enabled: v })} data-testid="invoice-reminders-enabled" />
        </div>
        <div className={`flex flex-wrap items-center gap-2 ${rem.enabled ? "" : "opacity-60"}`} data-testid="invoice-reminder-days">
          {rem.overdue_days.map((d) => (
            <Badge key={d} variant="secondary" className="gap-1 text-xs font-normal" data-testid={`invoice-reminder-day-${d}`}>
              {d} day{d === 1 ? "" : "s"} after due
              <button type="button" onClick={() => setRem({ ...rem, overdue_days: rem.overdue_days.filter((x) => x !== d) })} className="hover:text-red-600" aria-label={`Remove ${d}-day reminder`} data-testid={`invoice-reminder-remove-${d}`}><X size={11} /></button>
            </Badge>
          ))}
          <div className="flex items-center gap-1">
            <Input type="number" min="1" max="120" value={newDay} onChange={(e) => setNewDay(e.target.value)} placeholder="e.g. 14" className="h-7 w-24 text-xs" disabled={!rem.enabled} data-testid="invoice-reminder-new-day" />
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addDay} disabled={!rem.enabled} data-testid="invoice-reminder-add-day"><Plus size={12} className="mr-1" /> Add</Button>
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <Button size="sm" onClick={() => save({ invoice_reminders: rem }, "rem")} disabled={saving === "rem"} data-testid="invoice-reminders-save-btn">{saving === "rem" ? "Saving..." : "Save"}</Button>
        </div>
      </div>
    </>
  );
}
